import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';

function convertToRMB(amount, currency, exchangeRates) {
  if (currency === '¥' || currency === 'CNY') return amount;
  if (currency === '$' || currency === 'USD') return amount * (exchangeRates.USD || 7.19);
  return amount;
}

function Charts({ accounts, trades, selectedAccount, exchangeRates }) {
  // 数据准备
  const { dates, equityCurve, profitLoss, drawdown, profitLossDates, currency } = useMemo(() => {
    let dates = ['初始'];
    let equityCurve = [0];
    let drawdown = [0];
    let profitLossDates = [];
    let profitLoss = [];
    let currency = '¥';
    if (!selectedAccount) {
      // 所有账户汇总
      let accountBalances = {};
      let totalInitial = 0;
      accounts.forEach(acc => {
        const initial = convertToRMB(acc.capital, acc.currency, exchangeRates);
        totalInitial += initial;
        accountBalances[acc.name] = initial;
      });
      let currentTotal = totalInitial;
      let maxBalance = currentTotal;
      // 按日期分组
      const sortedTrades = [...trades].sort((a, b) => new Date(a.date) - new Date(b.date));
      const uniqueDates = [...new Set(sortedTrades.map(trade => trade.date))];
      uniqueDates.forEach(date => {
        // 该日所有交易
        const dayTrades = sortedTrades.filter(trade => trade.date === date);
        dayTrades.forEach(trade => {
          const acc = accounts.find(a => a.name === trade.account);
          if (!acc) return;
          const profitRMB = convertToRMB(trade.profit, acc.currency, exchangeRates);
          currentTotal += profitRMB;
        });
        maxBalance = Math.max(maxBalance, currentTotal);
        const percentage = ((currentTotal - totalInitial) / totalInitial * 100);
        const dd = maxBalance > 0 ? ((maxBalance - currentTotal) / maxBalance * 100) : 0;
        dates.push(date);
        equityCurve.push(percentage);
        drawdown.push(dd);
      });
      // 盈亏柱状图仍然按日期分组
      let dailyProfits = {};
      sortedTrades.forEach(trade => {
        const acc = accounts.find(a => a.name === trade.account);
        if (!acc) return;
        const profitRMB = convertToRMB(trade.profit, acc.currency, exchangeRates);
        dailyProfits[trade.date] = (dailyProfits[trade.date] || 0) + profitRMB;
      });
      profitLossDates = Object.keys(dailyProfits);
      profitLoss = profitLossDates.map(date => dailyProfits[date] || 0);
      currency = '¥';
    } else {
      // 单账户
      const acc = accounts.find(a => a.name === selectedAccount);
      if (acc) {
        currency = acc.currency || '¥';
        let balance = acc.capital;
        let maxBalance = balance;
        const filteredTrades = trades.filter(t => t.account === selectedAccount).sort((a, b) => new Date(a.date) - new Date(b.date));
        const uniqueDates = [...new Set(filteredTrades.map(trade => trade.date))];
        uniqueDates.forEach(date => {
          const dayTrades = filteredTrades.filter(trade => trade.date === date);
          dayTrades.forEach(trade => {
            balance += trade.profit;
          });
          maxBalance = Math.max(maxBalance, balance);
          const percentage = ((balance - acc.capital) / acc.capital * 100);
          const dd = maxBalance > 0 ? ((maxBalance - balance) / maxBalance * 100) : 0;
          dates.push(date);
          equityCurve.push(percentage);
          drawdown.push(dd);
        });
        // 盈亏柱状图仍然按日期分组
        let dailyProfits = {};
        filteredTrades.forEach(trade => {
          dailyProfits[trade.date] = (dailyProfits[trade.date] || 0) + trade.profit;
        });
        profitLossDates = Object.keys(dailyProfits);
        profitLoss = profitLossDates.map(date => dailyProfits[date] || 0);
      }
    }
    return { dates, equityCurve, profitLoss, drawdown, profitLossDates, currency };
  }, [accounts, trades, selectedAccount, exchangeRates]);

  // 资金曲线图
  const equityOption = {
    title: { text: '资金曲线 (%)', left: 'center' },
    tooltip: {
      trigger: 'axis',
      formatter: function(params) {
        const index = params[0].dataIndex;
        const date = dates[index];
        const percentage = params[0].value;
        // 余额估算
        let balance = '';
        if (!selectedAccount) {
          // 所有账户用人民币
          const initial = accounts.reduce((sum, acc) => sum + convertToRMB(acc.capital, acc.currency, exchangeRates), 0);
          balance = initial * (1 + percentage / 100);
          return `${date}<br/>收益率: ${percentage.toFixed(2)}%<br/>余额: ¥${balance.toFixed(2)}`;
        } else {
          const acc = accounts.find(a => a.name === selectedAccount);
          if (acc) {
            balance = acc.capital * (1 + percentage / 100);
            return `${date}<br/>收益率: ${percentage.toFixed(2)}%<br/>余额: ${acc.currency}${balance.toFixed(2)}`;
          }
        }
        return `${date}<br/>收益率: ${percentage.toFixed(2)}%`;
      }
    },
    xAxis: { type: 'category', data: dates },
    yAxis: { type: 'value', name: '增长率 (%)', axisLabel: { formatter: '{value}%' } },
    series: [{ type: 'line', data: equityCurve, smooth: true, lineStyle: { color: '#5470c6' } }],
  };
  // 盈亏柱状图
  const profitLossOption = {
    title: { text: `盈亏柱状图 (${currency})`, left: 'center' },
    tooltip: {
      trigger: 'axis',
      formatter: function(params) {
        const value = params[0].value;
        const date = params[0].name;
        return `${date}<br/>盈亏: ${currency}${Number(value).toFixed(2)}`;
      }
    },
    xAxis: { type: 'category', data: profitLossDates },
    yAxis: { type: 'value', name: `盈亏 (${currency})`, splitLine: { show: true } },
    series: [{
      type: 'bar',
      data: profitLoss,
      itemStyle: {
        color: params => params.value > 0 ? '#91cc75' : '#ee6666'
      }
    }],
  };
  // 回撤分析图
  const drawdownOption = {
    title: { text: '回撤分析 (%)', left: 'center' },
    tooltip: {
      trigger: 'axis',
      formatter: function(params) {
        const index = params[0].dataIndex;
        const date = dates[index];
        const drawdown = params[0].value;
        return `${date}<br/>回撤: ${Number(drawdown).toFixed(2)}%`;
      }
    },
    xAxis: { type: 'category', data: dates },
    yAxis: { type: 'value', name: '回撤 (%)', axisLabel: { formatter: '{value}%' }, inverse: true },
    series: [{
      type: 'line',
      data: drawdown,
      smooth: true,
      lineStyle: { color: '#ee6666' },
      areaStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(238,102,102,0.3)' },
            { offset: 1, color: 'rgba(238,102,102,0)' }
          ]
        }
      }
    }],
  };

  return (
    <div className="section">
      <h2>图表分析</h2>
      <div>资金曲线
        <div className="chart-container">
          <ReactECharts option={equityOption} style={{ width: '100%', height: 300 }} />
        </div>
      </div>
      <div>盈亏柱状图
        <div className="chart-container">
          <ReactECharts option={profitLossOption} style={{ width: '100%', height: 300 }} />
        </div>
      </div>
      <div>回撤分析
        <div className="chart-container">
          <ReactECharts option={drawdownOption} style={{ width: '100%', height: 300 }} />
        </div>
      </div>
    </div>
  );
}

export default Charts; 